/**
 * ========================================
 * CHATBOT API UTILITIES
 * Production-grade API client with:
 * - Timeout handling
 * - Retry logic with exponential backoff
 * - Request cancellation
 * - Rate limiting
 * - Error handling
 * - Caching
 * ========================================
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    DEFAULT_TIMEOUT: 30000, // Increased to 30 seconds for complex processing
    MAX_RETRIES: 2,
    RETRY_DELAY_BASE: 500,
    RATE_LIMIT_DELAY: 100, // Faster chat flow
    CACHE_TTL: 24 * 60 * 60 * 1000,
    ENABLE_LOGGING: true // Enabled for debugging payload/response issues
  };

  // In-memory cache
  const cache = new Map();
  const requestQueue = new Map();
  let lastChatRequestTime = 0;

  /**
   * Logging utility (disabled in production)
   */
  function log(...args) {
    if (CONFIG.ENABLE_LOGGING) console.log('[ChatbotAPI]', ...args);
  }

  function logError(...args) {
    if (CONFIG.ENABLE_LOGGING) console.error('[ChatbotAPI ERROR]', ...args);
  }

  /**
   * Sleep utility for delays
   */
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Calculate exponential backoff delay
   */
  function getRetryDelay(attempt) {
    return CONFIG.RETRY_DELAY_BASE * Math.pow(2, attempt);
  }

  /**
   * Check if data is still valid in cache
   */
  function isCacheValid(key) {
    const cached = cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < CONFIG.CACHE_TTL;
  }

  /**
   * Get cached data
   */
  function getCached(key) {
    if (isCacheValid(key)) {
      log('Cache hit:', key);
      return cache.get(key).data;
    }
    cache.delete(key);
    return null;
  }

  /**
   * Set cached data
   */
  function setCached(key, data) {
    cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
    log('Cache set:', key);
  }

  /**
   * Rate limiting for chat messages
   */
  function checkRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - lastChatRequestTime;

    if (timeSinceLastRequest < CONFIG.RATE_LIMIT_DELAY) {
      const waitTime = CONFIG.RATE_LIMIT_DELAY - timeSinceLastRequest;
      log('Rate limit: waiting', waitTime, 'ms');
      return waitTime;
    }

    lastChatRequestTime = now;
    return 0;
  }

  /**
   * Create fetch with timeout
   */
  function fetchWithTimeout(url, options = {}, timeout = CONFIG.DEFAULT_TIMEOUT) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

        const fetchPromise = fetch(url, {
      ...options,
      signal: controller.signal
    }).finally(() => {
      clearTimeout(timeoutId);
    });

    return fetchPromise;
  }

  /**
   * Retry wrapper with exponential backoff
   */
  async function fetchWithRetry(url, options = {}, retries = CONFIG.MAX_RETRIES, timeout = CONFIG.DEFAULT_TIMEOUT) {
    let lastError;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        if (attempt > 0) {
          const delay = getRetryDelay(attempt - 1);
          log(`Retry attempt ${attempt}/${retries} after ${delay}ms`);
          await sleep(delay);
        }

        const response = await fetchWithTimeout(url, options, timeout);

        // Don't retry on 4xx errors (client errors)
        if (!response.ok && response.status >= 400 && response.status < 500) {
          return response;
        }

        if (response.ok) {
          return response;
        }

        lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
      } catch (error) {
        lastError = error;

        // Don't retry on abort (timeout)
        if (error.name === 'AbortError') {
          throw new Error('Request timeout');
        }

        if (attempt === retries) {
          throw lastError;
        }
      }
    }

    throw lastError;
  }

  /** Cheap fingerprint for large POST bodies (avoids hashing multi‑MB base64 JSON end-to-end). */
  function requestBodyDedupFingerprint(bodyStr) {
    const s = String(bodyStr || '');
    const len = s.length;
    const tailStart = Math.max(0, len - 240);
    const tail = len ? s.slice(tailStart) : '';
    let h = 0;
    for (let i = 0; i < tail.length; i++) {
      h = ((h << 5) - h + tail.charCodeAt(i)) | 0;
    }
    const mid = Math.min(len >> 1, Math.max(0, len - 1));
    return len + ':' + mid + ':' + len.toString(16) + ':' + (h >>> 0).toString(16);
  }

  /**
   * Main API call function with all optimizations
   */
  async function apiCall(endpoint, options = {}, config = {}) {
    const {
      useCache = false,
      cacheKey = null,
      requireRateLimit = false,
      timeout = CONFIG.DEFAULT_TIMEOUT,
      retries = CONFIG.MAX_RETRIES
    } = config;

    // Check cache first
    if (useCache && cacheKey) {
      const cached = getCached(cacheKey);
      if (cached !== null) {
        return cached;
      }
    }

    // Rate limiting
    if (requireRateLimit) {
      const waitTime = checkRateLimit();
      if (waitTime > 0) {
        await sleep(waitTime);
      }
    }

    // Check for duplicate request in queue
    // For POST/PUT, we include the body string in the key to ensure different payloads are not treated as duplicates
    const bodyPrefixKey =
      (options.method === 'POST' || options.method === 'PUT') && options.body
        ? '_' + requestBodyDedupFingerprint(options.body)
        : '';
    const requestId = `${options.method || 'GET'}_${endpoint}${bodyPrefixKey}`;

    if (requestQueue.has(requestId)) {
      log('Duplicate request detected, waiting for existing request:', requestId);
      return requestQueue.get(requestId);
    }

    // Create request promise
    const requestPromise = (async () => {
      try {
        const response = await fetchWithRetry(endpoint, options, retries, timeout);

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`API error: ${response.status} - ${errorText}`);
        }

        const data = await response.json();

        // Cache successful responses
        if (useCache && cacheKey) {
          setCached(cacheKey, data);
        }

        return data;
      } finally {
        // Remove from queue
        requestQueue.delete(requestId);
      }
    })();

    // Add to queue
    requestQueue.set(requestId, requestPromise);

    return requestPromise;
  }

  /**
   * GET request helper
   */
  async function apiGet(url, config = {}) {
    return apiCall(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    }, config);
  }

  /**
   * POST request helper
   */
  async function apiPost(url, payload, config = {}) {
    return apiCall(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }, config);
  }

  /**
   * Clear cache
   */
  function clearCache(key = null) {
    if (key) {
      cache.delete(key);
    } else {
      cache.clear();
    }
  }

  /**
   * Cancel all pending requests
   */
  function cancelAllRequests() {
    requestQueue.clear();
  }

  // Public API
  window.ChatbotAPI = {
    get: apiGet,
    post: apiPost,
    clearCache: clearCache,
    cancelAll: cancelAllRequests,
    config: CONFIG
  };

})();
